import { useMemo } from 'react';
import { deriveTranslationStatus } from '@duke-hq/shared-constants/contentTranslationStatus';
import { trpcAdmin } from '@duke-hq/app-admin-web/lib/trpcAdmin';
import type { TranslatableResourceKind } from './useContentTranslations';

// Per-language readiness for a resource version, so a preview picker can grey out
// the languages it can't serve yet. Shared by the canvas simulation panel and the
// unit-guide / learning-material previews, which all derived this identically.
// "Servable" = renders in-language: available, available-partial, or stale (an
// older translated artifact still reads better than English).
export function useReadyByLanguage(
  resourceVersionId: number | null,
  resourceKind: TranslatableResourceKind,
  enabled: boolean,
): Map<string, boolean> {
  const availabilityQuery = trpcAdmin.contentTranslations.listByResourceVersion.useQuery(
    { resourceVersionId: resourceVersionId ?? 0, resourceKind },
    { enabled: enabled && resourceVersionId !== null },
  );

  return useMemo(() => {
    const map = new Map<string, boolean>();
    const currentPublishedVersionId = availabilityQuery.data?.currentPublishedVersionId ?? null;
    for (const row of availabilityQuery.data?.rows ?? []) {
      const status = deriveTranslationStatus(row, currentPublishedVersionId);
      map.set(
        row.language,
        status === 'available' || status === 'available-partial' || status === 'stale',
      );
    }
    return map;
  }, [availabilityQuery.data]);
}
